// ✅ Full restored version with `drill_order` field integrated and all original features kept.

import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, FormControl, InputLabel, Select,
  MenuItem, TextField, Button, Tabs, Tab, Alert, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Box
} from '@mui/material';
import AdminLayout from '../components/AdminLayout';
import { CopyDrillsModal, ReorderDrillsModal } from '../components/AdminCopyReorder';

const dayTabs = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Rest"];
const goalTypes = ["makes", "makes in a row", "no rim", "no rim in a row", "time"];
const courtLocations = ["left wing", "right wing", "top of the key", "free-throw line", "paint", "baseline", "elbow", "corner three", "full court"];
const dayTypes = ["odd", "even"];
const distances = ["2 ft", "5 ft", "10 ft", "15 ft", "3pt line"];
const goalValues = [1, 2, 3, 5, 7, 10, 15, 20];

const AdminBuildWorkoutPage = () => {
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [users, setUsers] = useState([]);
  const [drills, setDrills] = useState([]);
  const [message, setMessage] = useState('');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [drillToDelete, setDrillToDelete] = useState(null);
  const token = localStorage.getItem('token');
  const [weekPlan, setWeekPlan] = useState({ Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Rest: [] });

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [copiedDrills, setCopiedDrills] = useState([]);
  const allWeeks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const [copiedDrillsMetadata, setCopiedDrillsMetadata] = useState(null);

  const [form, setForm] = useState({
    user_id: '',
    drill_id: '',
    movement_or_distance: '',
    goal_type: '',
    goal_value: '',
    drill_order: '',
    court_location: '',
    progression_level: '',
    week_number: '',
    day_name: 'Monday',
    day_type: '',
    notes: '',
    assigned_by: 'admin'
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const handleOpenPreview = () => setPreviewOpen(true);
  const handleClosePreview = () => setPreviewOpen(false);

  useEffect(() => {
    fetch('http://localhost:8000/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json()).then(data => setUsers(data));

    fetch('http://localhost:8000/master_drill_list', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json()).then(data => setDrills(data));
  }, [token]);

  useEffect(() => {
    if (!form.user_id || !form.week_number) return;

    fetch(`http://localhost:8000/custom_workout_steps/week/${form.user_id}/${form.week_number}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const newWeekPlan = { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Rest: [] };
        data.forEach(drill => {
          if (newWeekPlan[drill.day_name]) newWeekPlan[drill.day_name].push(drill);
        });
        setWeekPlan(newWeekPlan);
        setMessage("📥 Loaded existing week plan from server.");
      })
      .catch(err => console.error("Error fetching existing week plan:", err));
  }, [form.user_id, form.week_number, token]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: ["goal_value", "progression_level", "week_number", "drill_order"].includes(name) ? Number(value) : value
    }));
  };

  const handleOpenCopyModal = () => {
    if (!form.week_number) {
      setMessage("Please select a week number before copying.");
      return;
    }
    setCopyModalOpen(true);
  };

  const handleAddDrill = async () => {
    const drillDetails = drills.find(d => d.id === form.drill_id);
    const drillName = drillDetails?.drill_name || '';
    
    const newDrill = {
      ...form,
      day_name: selectedDay,
      drill_name: drillName,
      movement_or_distance: form.movement_or_distance
    };
  
    console.log("Submitting drill:", newDrill);
  
    try {
      let response;
      if (form.id) {
        // Editing an existing drill
        response = await fetch(`http://localhost:8000/custom_workout_steps/${form.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newDrill)
        });
      } else {
        // Creating a new drill
        response = await fetch('http://localhost:8000/custom_workout_steps', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newDrill)
        });
      }
  
      if (!response.ok) throw new Error(`Failed to save drill: ${response.status}`);
  
      setWeekPlan(prev => {
        const updatedDay = [...prev[selectedDay]];
        const updatedDrill = {
          ...newDrill,
          id: form.id || Date.now()  // Use existing ID if editing, otherwise use Date.now()
        };
  
        if (editingIndex !== null) {
          updatedDay[editingIndex] = updatedDrill;
        } else {
          updatedDay.push(updatedDrill);
        }
  
        return { ...prev, [selectedDay]: updatedDay };
      });
  
      setForm(prev => ({
        ...prev,
        id: null, // Clear ID after edit
        drill_id: '',
        movement_or_distance: '',
        goal_type: '',
        goal_value: '',
        court_location: '',
        progression_level: '',
        notes: '',
        drill_order: ''
      }));
  
      setEditingIndex(null);
      setMessage(`Drill ${form.id ? 'updated' : 'added'} and saved to ${selectedDay}`);
    } catch (err) {
      console.error(err);
      setMessage(`❌ Error: Could not save drill. Please try again.`);
    }
  };
  
    

  const handleTabChange = (event, newValue) => {
    const day = dayTabs[newValue];
    setSelectedDay(day);
    setSelectedTabIndex(newValue);
    setForm(prev => ({ ...prev, day_name: day }));
  };

  const handleDeleteDrill = (drill, index) => {
    console.log("Deleting drill with ID:", drill.id);
    setDrillToDelete({ ...drill, index });
    setConfirmDeleteOpen(true);
  };

  const handleCopyConfirm = async (reorderedDrills, metadata = {}) => {
    const { targetDay, targetWeek } = metadata;
  
    if (!targetDay || !targetWeek) {
      console.error("Missing targetDay or targetWeek in metadata:", metadata);
      setMessage("❌ Copy failed: Invalid target day or week.");
      return;
    }
    
    const drillIdMap = {
      "3. Jump Shot": 1,
      "4. Curl Right Wing (Self Throw)": 2,
      "8. Jab Step Drive Left (Left Wing)": 3,
      "5. Curl Left Wing (Self Throw)": 4,
      "4. Reverse Pivot Pull ups (Start at 3pt line) Drive Left": 5,
      "9. Jab Step Drive Right(Left Wing)": 6,
      "8. Free-Throws": 7,
      "7. Reverse Pivot pull ups cross-over(Start at 3pt line)Drive left/Crossover right": 8,
      "5. Reverse Pivot Pull ups(Start at 3pt line) Drive Right": 9,
      "10. Free-Throws": 10,
      "6. Reverse Pivot pull ups cross-over(Start at 3pt line)Drive right/Crossover left": 11,
      "2. Two-hand form shooting": 12,
      "7. Jab Step Drive Right(Right Wing)": 13,
      "6. Jab Step Drive Left (Right Wing)": 14,
      "1.One-hand form shooting": 15
    };
    
    
    const finalDrills = reorderedDrills.map((drill, idx) => {
      const { id, created_at, ...rest } = drill;
    
      const resolvedDrillId = drill.drill_id ?? drillIdMap[drill.drill_name?.trim()];
    
      console.log(`🔍 Resolving "${drill.drill_name}" → ID:`, resolvedDrillId);
    
      return {
        ...rest,
        drill_id: resolvedDrillId,
        drill_name: drill.drill_name,
        day_name: targetDay,
        week_number: targetWeek,
        drill_order: idx + 1,
        assigned_by: 'admin'
      };
    });    
    
  
    setWeekPlan(prev => ({
      ...prev,
      [targetDay]: finalDrills
    }));
  
    try {
      const token = localStorage.getItem('token');
      const responses = await Promise.all(
        finalDrills.map(drill => {
          console.log("Saving drill to backend:", drill); // ✅ this stays
          console.log("Fields in drill before POST:", Object.keys(drill));
          return fetch('http://localhost:8000/custom_workout_steps/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(drill)
          });
        })
      );
  
      const allSuccessful = responses.every(res => res.ok);
      setMessage(allSuccessful
        ? `✅ Drills copied and saved to ${targetDay} in Week ${targetWeek}.`
        : `⚠️ Some drills failed to save to backend.`);
    } catch (error) {
      console.error("Copy error:", error);
      setMessage("❌ Failed to save copied drills to backend.");
    }
  
    setReorderModalOpen(false);
    setCopyModalOpen(false);
  };  
    
  
  const confirmAndDeleteDrill = async () => {
    if (!drillToDelete) return;
  
    const { id, index } = drillToDelete;
  
    // Skip DELETE API call if there's no real DB id
    if (!id || typeof id !== 'number') {
      console.warn("Drill has no valid backend ID. Skipping API delete.");
      removeDrillFromFrontend(index);
      return;
    }
  
    try {
      const response = await fetch(`http://localhost:8000/custom_workout_steps/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
  
      if (!response.ok) throw new Error("Backend delete failed");
  
      removeDrillFromFrontend(index);
      setMessage("🗑️ Drill deleted successfully.");
    } catch (err) {
      console.error("Delete error:", err);
      setMessage("❌ Could not delete drill.");
    } finally {
      setConfirmDeleteOpen(false);
      setDrillToDelete(null);
    }
  };
  
  const removeDrillFromFrontend = (index) => {
    setWeekPlan(prev => {
      const updatedDay = [...prev[selectedDay]];
      updatedDay.splice(index, 1);
      return { ...prev, [selectedDay]: updatedDay };
    });
  };
  
  

  const handleLoadWeek = async () => {
    try {
      const res = await fetch(`http://localhost:8000/custom_workout_steps/week/${form.user_id}/${form.week_number}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to load drills.");

      const drills = await res.json();
      const grouped = { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Rest: [] };
      drills.forEach(d => grouped[d.day_name || "Rest"].push(d));
      setWeekPlan(grouped);
      setMessage(`✅ Loaded drills for Week ${form.week_number}`);
    } catch (err) {
      console.error("Load failed:", err);
      setMessage("❌ Failed to load saved drills.");
    }
  };

  const handleSubmitFullWeek = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const drillsToSubmit = [];
    Object.entries(weekPlan).forEach(([day, drills]) => {
      drills.forEach(drill => {
        // Only submit drills without a valid backend ID (i.e., newly added)
        if (!drill.id || drill.id.toString().length > 10) {
          drillsToSubmit.push({
            ...drill,
            day_name: day,
            assigned_by: 'admin',
          });
        }
      });
    });
    if (drillsToSubmit.length === 0) {
      setMessage("⚠️ No new drills to submit.");
      return;
    } 
    try {
      const responses = await Promise.all(drillsToSubmit.map(drill =>
        fetch('http://localhost:8000/custom_workout_steps/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(drill)
        })
      ));
      const allSuccessful = responses.every(res => res.ok);
      setMessage(allSuccessful
        ? "✅ All drills submitted successfully for the week!"
        : "⚠️ Some drills failed. Check backend.");  
      if (allSuccessful) {
        setConfirmClearOpen(true);
        setEditingIndex(null);
      }
    } catch (err) {
      console.error("Batch submit error:", err);
      setMessage("❌ Error submitting drills.");
    }
  };
  


  return (
    <>
    <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this drill?</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
        <Button onClick={confirmAndDeleteDrill} color="error">Delete</Button>
    </DialogActions>
    </Dialog>

    <AdminLayout>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>Build Weekly Workout</Typography>
        {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

        <Tabs value={selectedTabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
          {dayTabs.map((day, i) => <Tab key={i} label={day} />)}
        </Tabs>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            {/* Drill Form Inputs */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Player</InputLabel>
              <Select name="user_id" value={form.user_id} onChange={handleFormChange}>
                {users.map(user => <MenuItem key={user.id} value={user.id}>{user.name} (ID: {user.id})</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Drill</InputLabel>
              <Select name="drill_id" value={form.drill_id} onChange={handleFormChange}>
                {drills.map(drill => <MenuItem key={drill.id} value={drill.id}>{drill.drill_name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Goal Type</InputLabel>
              <Select name="goal_type" value={form.goal_type} onChange={handleFormChange}>
                {goalTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Goal Value</InputLabel>
              <Select name="goal_value" value={form.goal_value} onChange={handleFormChange}>
                {goalValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Drill Order" name="drill_order" type="number" value={form.drill_order} onChange={handleFormChange} sx={{ mb: 2 }} />
            <TextField fullWidth label="Progression Level" name="progression_level" type="number" value={form.progression_level} onChange={handleFormChange} sx={{ mb: 2 }} />
            <TextField fullWidth label="Week Number" name="week_number" type="number" value={form.week_number} onChange={handleFormChange} sx={{ mb: 2 }} />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Movement/Distance</InputLabel>
              <Select name="movement_or_distance" value={form.movement_or_distance} onChange={handleFormChange}>
                {distances.map(dist => <MenuItem key={dist} value={dist}>{dist}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Court Location</InputLabel>
              <Select name="court_location" value={form.court_location} onChange={handleFormChange}>
                {courtLocations.map(loc => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Movement/Distance</InputLabel>
              <Select name="movement_or_distance" value={form.movement_or_distance} onChange={handleFormChange}>
                {distances.map(dist => (
               <MenuItem key={dist} value={dist}>{dist}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Day Type</InputLabel>
              <Select name="day_type" value={form.day_type} onChange={handleFormChange}>
                {dayTypes.map(dt => <MenuItem key={dt} value={dt}>{dt}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth multiline rows={2} label="Notes" name="notes" value={form.notes} onChange={handleFormChange} sx={{ mb: 2 }} />
            <Button fullWidth variant="contained" onClick={handleAddDrill} disabled={!form.user_id || !form.drill_id || !form.week_number}>
              Add Drill to {selectedDay}
            </Button>
          </Grid>

          <Grid item xs={12} md={8}>
            <Button variant="outlined" onClick={handleOpenCopyModal} sx={{ mb: 2 }}> 
              Copy This Day To... 
            </Button>

            <Typography variant="h6">Drills for {selectedDay}</Typography>
            {weekPlan[selectedDay].length === 0 ? <Typography>No drills added yet.</Typography> : (
              weekPlan[selectedDay].map((drill, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Typography><strong>{drill.drill_name}</strong> (Order: {drill.drill_order ?? 'N/A'})</Typography>
                  <Typography variant="body2">Goal: {drill.goal_type} {drill.goal_value}</Typography>
                  <Typography variant="body2">Location: {drill.court_location}</Typography>
                  <Typography variant="body2">Level: {drill.progression_level}</Typography>
                  <Typography variant="body2">Distance: {drill.movement_or_distance}</Typography>
                  <Typography variant="body2">Notes: {drill.notes}</Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => {
                      setForm(prev => ({ ...prev, ...drill }));
                      setEditingIndex(index);
                    }}>Edit</Button>
                    <Button variant="outlined" size="small" color="error" onClick={() => handleDeleteDrill(drill, index)}>Delete</Button>
                  </Box>
                </Paper>
              )))}

            <Button variant="contained" color="success" onClick={handleSubmitFullWeek} sx={{ mt: 2 }}>
              Submit Full Week
            </Button>
            <Button variant="outlined" onClick={handleOpenPreview} sx={{ mt: 2, ml: 2 }}>
              Preview Full Week
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleLoadWeek} sx={{ mt: 2, ml: 2 }}>
              Reload Week
            </Button>
          </Grid>
        </Grid>
      </Container>

      <CopyDrillsModal
        open={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        currentDay={selectedDay}
        currentWeek={form.week_number}
        drillsToCopy={weekPlan[selectedDay] || []}
        onConfirm={handleCopyConfirm}
        allDays={dayTabs}
        allWeeks={allWeeks}
      />

      <ReorderDrillsModal
        open={reorderModalOpen}
        onClose={() => setReorderModalOpen(false)}
        drills={copiedDrills}
        onSave={(reordered) => {
          setCopiedDrills(reordered);
          handleCopyConfirm(reordered, copiedDrillsMetadata);
        }}
      />

    </AdminLayout>
  </>
  );
};

export default AdminBuildWorkoutPage;
